import React, { useEffect, useState, useContext } from "react";
import {
  getAllNotifications,
  postNotification,
  deleteNotification,
} from "../../../../../libs/api/notification";
import { toast } from "react-toastify";
import { IoClose } from "react-icons/io5";
import { MdDelete } from "react-icons/md";
import LoadingSpinner from "../../../../../libs/LoadingSpinner";
import Pagination from "../../../../../libs/Pagination";
import { AppContext } from "../../../../../context/appContext";

const initialFormData = {
  title: "",
  body: "",
};

const initialErrors = {
  title: "",
  body: "",
};

export default function NotificationSender() {
  const { t, formatNum, isRTL } = useContext(AppContext);
  const [notificationsDataArr, setNotificationsDataArr] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [errors, setErrors] = useState();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalPages: 0,
    totalRecords: 0,
  });
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const clearForm = () => {
    setFormData(initialFormData);
    setErrors(initialErrors);
  };

  const openModal = (mode, idx = null) => {
    setModalMode(mode);
    setSelectedIdx(idx);

    if (mode === "edit" && idx !== null) {
      setFormData(notificationsDataArr[idx]);
    } else {
      clearForm();
    }

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    clearForm();
    setSelectedIdx(null);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = t('notification.titleRequired');
    if (!formData.body.trim()) newErrors.body = t('notification.bodyRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  const postNotificationFunc = async (payload) => {
    setIsAddLoading(true);
    try {
      const res = await postNotification(payload);
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.data?.message || t('notification.notificationSent'));
      } else {
        toast.error(res?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setIsAddLoading(false);
    }
  };

    // Handle page change
    const handlePageChange = (newPage) => {
      if (newPage > 0 && newPage <= pagination.totalPages) {
        setPagination({ ...pagination, currentPage: newPage });
      }
    };

  const getNotificationFunc = async (pagination) => {
    if (notificationsDataArr.length === 0 || !notificationsDataArr) {
      setIsLoading(true);
    }
    try {
      const res = await getAllNotifications(pagination);
      if (res?.status === 200) {
        setNotificationsDataArr(res?.data?.data || []);
        setPagination((prev) => ({
          ...prev,
          totalPages: res?.data?.totalPages || 0,
          totalRecords: res?.data?.totalRecords || 0,
        }));
      } else {
        toast.error(res?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    await postNotificationFunc(formData);
    closeModal();
    await getNotificationFunc(pagination);
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    setIsDeleting(true);
    try {
      const res = await deleteNotification(deleteConfirmId);
      if (res?.status === 200) {
        toast.success(res?.data?.message || t('notification.notificationDeleted'));
        setDeleteConfirmId(null);
        await getNotificationFunc(pagination);
      } else {
        toast.error(res?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    getNotificationFunc(pagination);
  }, [pagination.currentPage]);

  return (
    <div className="p-6" dir={isRTL ? "rtl" : "ltr"}>
      <h2 className="text-2xl font-semibold mb-4">{t('notification.title')}</h2>
      <div className="bg-white rounded-3xl shadow">
        {/* Search and Add Language */}
        <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} items-center p-6 pt-7`}>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2 border-0 rounded-xl hover:bg-blue-700"
          >
            {t('notification.sendNotification')}
          </button>
        </div>

        {/* Table */}
        {notificationsDataArr.length === 0 && !isLoading ? (
          <div className="flex justify-center items-center h-full p-6">
            <p className="text-gray-500">{t('notification.noNotifications')}</p>
          </div>
        ) : notificationsDataArr.length === 0 && isLoading ? (
          <div className="flex justify-center items-center h-full p-6">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="w-full">
            {/* Table Header */}
            <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-4 border-b border-[#F7F7F9]">
              <div className={`p-2 py-4 ${isRTL ? 'pr-8' : 'pl-8'}`}>{t('notification.notificationTitle')}</div>
              <div className="p-2 py-4 col-span-2">{t('notification.notificationBody')}</div>
              <div className="p-2 py-4 text-center">{t('common.actions')}</div>
            </div>
            <div>
              {notificationsDataArr.map((elem, idx) => (
                <div
                  key={elem?._id}
                  className="grid grid-cols-4 hover:bg-gray-50 border-b border-[#efeff1] text-sm font-[500] select-none"
                >
                  <div className={`p-2 py-6 ${isRTL ? 'pr-8' : 'pl-8'} flex items-center capitalize`}>
                    {elem?.title}
                  </div>
                  <div className="p-2 py-6 flex items-center col-span-2">{elem?.body}</div>
                  <div className="p-2 py-6 flex items-center justify-center">
                    <button
                      onClick={() => handleDeleteClick(elem?._id)}
                      className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                      title={t('common.delete')}
                    >
                      <MdDelete size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && notificationsDataArr.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((pagination.currentPage - 1) * pagination.limit + 1)} {t('pagination.to')}{" "}
              {formatNum(Math.min(
                pagination.currentPage * pagination.limit,
                pagination.totalRecords
              ))}{" "}
              {t('pagination.of')} {formatNum(pagination.totalRecords)} {t('pagination.entries')}
            </span>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl min-w-[400px] max-w-[95vw] w-full md:w-[400px] p-0 shadow-lg" dir={isRTL ? "rtl" : "ltr"}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-lg">{t('notification.sendNotification')}</h3>
                <button
                  className="text-xl hover:text-red-500"
                  onClick={closeModal}
                >
                  <IoClose />
                </button>
              </div>
              {/* Title */}
              <label className="block text-sm font-medium mb-2">{t('notification.notificationTitle')}</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder={t('notification.enterTitle')}
                className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
              />
              {errors.title && (
                <p className="text-red-500 text-xs m-0 p-0 mb-2">
                  {errors.title}
                </p>
              )}

              <label className="block text-sm font-medium mb-2">{t('notification.notificationBody')}</label>
              <textarea
                type="text"
                name="body"
                value={formData.body}
                onChange={handleInputChange}
                placeholder={t('notification.enterBody')}
                className="w-full mb-0 border rounded-xl p-3 border-[#808080]"
              />
              {errors.body && (
                <p className="text-red-500 text-xs m-0 mb-0">{errors.body}</p>
              )}
            </div>
            <div className={`modal-action rounded-b-2xl bg-[#fafafa] mt-2 p-6 py-4 flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
              <button
                type="button"
                disabled={isAddLoading}
                onClick={handleSubmit}
                className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700"
              >
                {isAddLoading ? (
                  <LoadingSpinner />
                ) : modalMode === "add" ? (
                  t('notification.send')
                ) : (
                  t('common.update')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl min-w-[400px] max-w-[95vw] w-full md:w-[400px] p-6 shadow-lg" dir={isRTL ? "rtl" : "ltr"}>
            <h3 className="font-semibold text-lg mb-4">{t('notification.deleteConfirmation')}</h3>
            <p className="text-gray-600 mb-6">{t('notification.deleteConfirmationMessage')}</p>
            <div className={`flex gap-3 ${isRTL ? 'justify-start' : 'justify-end'}`}>
              <button
                onClick={handleDeleteCancel}
                disabled={isDeleting}
                className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <LoadingSpinner />
                    {t('common.deleting')}
                  </>
                ) : (
                  t('common.delete')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
